import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpenses, useDeleteExpense, useExpenseEditHistory } from '@/hooks/useExpenses';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Trash2, ChevronLeft, ChevronRight, History } from 'lucide-react';
import { format } from 'date-fns';
import { fr, arSA } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { Expense, AssistantPermissions } from '@/types/entities';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { AlertModal } from '@/components/ui/alert-modal';
import { AccountingEditHistoryPanel } from './AccountingEditHistoryPanel';
import type { ExpenseWithAudit } from '@/hooks/useExpenses';

interface ExpensesTableProps {
  restaurantId: string;
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  onEdit: (expense: Expense) => void;
  /** Filter mode: 'all' | 'salaries_only' | 'expenses_only' */
  filterMode?: 'all' | 'salaries_only' | 'expenses_only';
}

export function ExpensesTable({
  restaurantId,
  startDate,
  endDate,
  categoryId,
  onEdit,
  filterMode = 'all',
}: ExpensesTableProps) {
  const { t, i18n } = useTranslation();
  const { data: assistant } = useCurrentAssistant();
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Permission helper for assistants
  const hasPermission = (permPage: keyof AssistantPermissions, action: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[permPage]) return false;
    const pagePermissions = assistant.permissions[permPage] as Record<string, boolean>;
    return pagePermissions[action] === true;
  };

  // Check if user is an assistant
  const isAssistant = !!assistant;

  // Permission checks (admins always have permission)
  const canEdit = !isAssistant || hasPermission('expenses', 'edit');
  const canDelete = !isAssistant || hasPermission('expenses', 'delete');

  // Reset page when category filter changes
  useEffect(() => {
    setPage(0);
  }, [categoryId]);

  const filters: any = {
    limit: pageSize,
    offset: page * pageSize,
    includeDeleted: true,
  };
  if (startDate) filters.startDate = startDate;
  if (endDate) filters.endDate = endDate;
  if (categoryId) filters.categoryId = categoryId;

  const { data, isLoading, refetch } = useExpenses(restaurantId, filters);

  const deleteExpenseMutation = useDeleteExpense(restaurantId);
  const [deletingExpenseId, setDeletingExpenseId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });
  const [historyExpenseId, setHistoryExpenseId] = useState<string | null>(null);

  // Determine if the selected expense is a salary payment
  const historyExpense = historyExpenseId
    ? (data?.expenses || []).find((e) => e.id === historyExpenseId)
    : null;
  const isSalaryPayment = !!historyExpense?.employeeId;

  // Get edit history for selected expense
  const { data: editHistory, isLoading: historyLoading } = useExpenseEditHistory(
    restaurantId,
    historyExpenseId,
    isSalaryPayment
  );

  const locale = i18n.language === 'ar' ? arSA : fr;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleDelete = (expense: Expense) => {
    setConfirmDialog({
      open: true,
      title: t('accounting.deleteExpense'),
      description: t('accounting.deleteExpenseConfirm'),
      onConfirm: async () => {
        setDeletingExpenseId(expense.id);
        try {
          await deleteExpenseMutation.mutateAsync(expense.id);
          await refetch();
        } catch (error) {
          console.error('Failed to delete expense:', error);
          setAlertDialog({
            open: true,
            title: t('error.error'),
            description: t('error.generic'),
          });
        } finally {
          setDeletingExpenseId(null);
        }
      },
    });
  };

  // Filter expenses based on filterMode
  const filteredExpenses = (data?.expenses || []).filter((expense) => {
    if (filterMode === 'all') return true;
    if (filterMode === 'salaries_only') return !!expense.employeeId;
    if (filterMode === 'expenses_only') return !expense.employeeId;
    return true;
  });

  const totalCount = filteredExpenses.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Paginate filtered expenses
  const paginatedExpenses = filteredExpenses.slice(page * pageSize, (page + 1) * pageSize);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (filteredExpenses.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">{t('accounting.noExpenses')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {paginatedExpenses.map((expense) => {
          const expenseWithAudit = expense as ExpenseWithAudit;
          const isDeleted = !!expenseWithAudit.deletedAt;

          return (
            <Card
              key={expense.id}
              className={cn('overflow-hidden', isDeleted && 'opacity-60 bg-muted/30')}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {isDeleted && (
                        <Badge variant="destructive" className="text-xs">
                          {t('accounting.deleted')}
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={cn(isDeleted && 'line-through')}
                        style={{
                          borderColor: expense.categoryColor,
                          color: expense.categoryColor,
                        }}
                      >
                        {i18n.language === 'ar' ? expense.categoryNameAr : expense.categoryNameFr}
                      </Badge>
                      <span
                        className={cn(
                          'text-sm text-muted-foreground',
                          isDeleted && 'line-through'
                        )}
                      >
                        {format(new Date(expense.expenseDate), 'dd/MM/yyyy', { locale })}
                      </span>
                    </div>
                    <p className={cn('font-medium', isDeleted && 'line-through')}>
                      {i18n.language === 'ar' ? expense.descriptionAr : expense.descriptionFr}
                      {!expense.descriptionAr && !expense.descriptionFr && (
                        <span className="text-muted-foreground">
                          {t('accounting.noDescription')}
                        </span>
                      )}
                    </p>
                    {expense.employeeNameAr && (
                      <p
                        className={cn(
                          'text-sm text-muted-foreground',
                          isDeleted && 'line-through'
                        )}
                      >
                        {t('accounting.employee')}:{' '}
                        {i18n.language === 'ar' ? expense.employeeNameAr : expense.employeeNameFr}
                      </p>
                    )}
                    {expense.referenceNumber && (
                      <p
                        className={cn(
                          'text-xs text-muted-foreground',
                          isDeleted && 'line-through'
                        )}
                      >
                        {t('accounting.reference')}: {expense.referenceNumber}
                      </p>
                    )}
                    {expenseWithAudit.createdByDisplay && (
                      <p className="text-xs text-muted-foreground italic mt-1">
                        {t('accounting.createdBy')}: {expenseWithAudit.createdByDisplay}
                      </p>
                    )}
                    {isDeleted && expenseWithAudit.updatedByDisplay && (
                      <p className="text-xs text-muted-foreground italic mt-1">
                        {t('accounting.deletedBy')}: {expenseWithAudit.updatedByDisplay}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'text-lg font-bold text-red-600',
                        isDeleted && 'line-through'
                      )}
                    >
                      -{formatCurrency(expense.amount)} {t('common.currency')}
                    </span>
                    {!isDeleted && canEdit && (
                      <Button variant="ghost" size="icon" onClick={() => onEdit(expense)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {!isDeleted && canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(expense)}
                        disabled={deletingExpenseId === expense.id}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setHistoryExpenseId(expense.id)}
                      title={t('accounting.editHistory.title')}
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t">
          <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left order-2 sm:order-1">
            {t('common.showing')} {page * pageSize + 1}-
            {Math.min((page + 1) * pageSize, totalCount)} {t('common.of')} {totalCount}
          </p>
          <div className="flex items-center gap-2 order-1 sm:order-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="h-9"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-muted-foreground min-w-[60px] text-center">
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="h-9"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant="destructive"
      />

      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
      />

      {/* Edit History Dialog */}
      <Dialog open={!!historyExpenseId} onOpenChange={(open) => !open && setHistoryExpenseId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('accounting.editHistory.title')}</DialogTitle>
          </DialogHeader>
          <AccountingEditHistoryPanel
            entries={editHistory || []}
            isLoading={historyLoading}
            type="expense"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
